import React from "react";
import { CalendarClock, AlertTriangle, CheckCircle2, Calendar } from "lucide-react";
import { useT } from "../context/LanguageContext";

/**
 * Returns one of:
 *  - "PLAYED"   (status confirmed)
 *  - "OVERDUE"  (scheduled in past + still pending/disputed)
 *  - "TODAY"    (scheduled today + still pending/disputed)
 *  - "UPCOMING" (scheduled future + still pending/disputed)
 *  - "UNSCHEDULED"
 */
export function classifyMatch(match) {
  if (!match) return "UNSCHEDULED";
  if (match.status === "confirmed" || match.admin_override) return "PLAYED";
  if (!match.scheduled_at) return "UNSCHEDULED";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sched = new Date(match.scheduled_at + "T00:00:00");
  if (sched.getTime() < today.getTime()) return "OVERDUE";
  if (sched.getTime() === today.getTime()) return "TODAY";
  return "UPCOMING";
}

export function formatScheduleDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric",
  });
}

export default function ScheduleBadge({ match, className = "" }) {
  const cls = classifyMatch(match);
  if (cls === "UNSCHEDULED") return null;

  const config = {
    PLAYED:    { color: "text-[#34C759] border-[#34C759]/40", Icon: CheckCircle2, label: "PLAYED" },
    OVERDUE:   { color: "text-[#E11D48] border-[#E11D48] bg-[#E11D48]/5", Icon: AlertTriangle, label: "OVERDUE" },
    TODAY:     { color: "text-[#7C3AED] border-[#7C3AED] bg-[#7C3AED]/5", Icon: CalendarClock, label: "TODAY" },
    UPCOMING:  { color: "text-[#71717A] border-[#E4E4E7] dark:border-[#2A2A30]", Icon: Calendar, label: formatScheduleDate(match.scheduled_at).toUpperCase() },
  }[cls];
  const { Icon, color, label } = config;

  return (
    <span
      className={`inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] ${color} ${className}`}
      data-testid="schedule-badge"
      title={match.scheduled_at ? `Scheduled ${formatScheduleDate(match.scheduled_at)}` : ""}
    >
      <Icon className="w-3 h-3" />
      {label}
      {cls !== "TODAY" && cls !== "UPCOMING" && match.scheduled_at && (
        <span className="text-[#A1A1AA] font-mono normal-case">· {formatScheduleDate(match.scheduled_at)}</span>
      )}
    </span>
  );
}
